import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useAppDispatch } from "@/store/hooks";
import { updateTask } from "@/store/taskSlice";
import {
  clampMinutes,
  formatMinutes,
  isValidTimeExpression,
  parseTimeExpression,
} from "@/utils/timeTracking";

const EditableEstimateField: React.FC<{
  value: number;
  label: string;
  field: "original_estimate_minutes" | "remaining_time_minutes";
  taskId: string;
  onUpdateFields?: (taskId: string, updates: any) => Promise<void>;
  isSubtask?: boolean; // Flag to indicate if this is for a subtask
  isviewerOnly?: boolean;
}> = ({ value, label, field, taskId, onUpdateFields, isSubtask = false, isviewerOnly }) => {
  const dispatch = useAppDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(formatMinutes(value));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    setInputValue(formatMinutes(value));
  }, [value]);

  // Validation logic
  const validateInput = (str: string): boolean => {
    const normalized = str.trim().toLowerCase();
    if (!normalized) {
      setError("Time cannot be empty");
      return false;
    }

    // valid pattern like 1w 2d 3h 30m
    if (!isValidTimeExpression(normalized)) {
      setError("Invalid format. Use w/d/h/m (e.g. 1w 2d 3h 30m)");
      return false;
    }

    if (normalized.includes("-")) {
      setError("Negative values are not allowed");
      return false;
    }

    const totalMinutes = parseTimeExpression(normalized);
    if (totalMinutes <= 0) {
      setError("Time must be greater than zero");
      return false;
    }

    if (totalMinutes > 999999) {
      setError("Time value too large");
      return false;
    }

    setError("");
    return true;
  };

  const saveValue = async () => {
    if (!validateInput(inputValue)) return;
    const minutes = clampMinutes(parseTimeExpression(inputValue));
    setLoading(true);
    try {
      // For subtasks, only use onUpdateFields (which calls onSave -> updateSubtask)
      // For tasks, call updateTask and then onUpdateFields for refresh
      if (isSubtask && onUpdateFields) {
        await onUpdateFields(taskId, { [field]: minutes });
      } else {
        await dispatch(updateTask({ id: taskId, data: { [field]: minutes } })).unwrap();
        
        // Notify parent to refresh task (don't call updateTask again to avoid duplicate toast)
        if (onUpdateFields) {
          await onUpdateFields(taskId, { [field]: minutes });
        }
      }
      
      // Don't show toast here - updateTask/updateSubtask thunk already shows success message
      setIsEditing(false);
    } catch (err: any) {
      toast.error("Failed to update " + label.toLowerCase());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col p-2">
      <div className="flex items-center gap-2">
        {isEditing ? (
          <>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="e.g. 2h 30m"
              className={`border rounded px-2 py-1 text-sm w-28 ${
                error ? "border-red-400" : "border-gray-300"
              }`}
            />
            <button
              onClick={saveValue}
              disabled={loading}
              className="text-blue-500 hover:text-blue-700 text-sm"
            >
              Save
            </button>
            <button
              onClick={() => {
                setIsEditing(false);
                setError("");
              }}
              className="text-gray-400 hover:text-gray-700 text-sm"
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <span className="text-sm text-gray-900">{formatMinutes(value) || "Not set"}</span>
            {!isviewerOnly && (
            <button
              onClick={() => setIsEditing(true)}
              className="text-blue-500 hover:text-blue-700 text-sm"
            >
              Edit
            </button>
            )}
          </>
        )}
      </div>
      {error && <span className="text-xs text-red-500 mt-1">{error}</span>}
    </div>
  );
};

export default EditableEstimateField;
